<script>
import { mapActions } from 'vuex';
import Card from "../../../components/Card.vue";

export default {
    components: { Card },

    data() {
        return {
            categories: [],
            selectedCategoryId: 0,
            kpis: [],
            suppliers: [],

            // assignedMap: Map<metric_kpi_id, Set<supplier_company_id>>
            // Individual (per-supplier) assignments. Empty for metrics in "all suppliers" mode.
            assignedMap: new Map(),

            // allMap: Map<metric_kpi_id, boolean> — whether the metric is requested for ALL suppliers
            // (the wildcard). When true, any current OR future eligible supplier is treated as requested.
            allMap: new Map(),

            openSupplierDropdownMetricId: null,

            // Single, primary-wide "from year": suppliers are asked to supply from this year onward.
            requestFromYear: new Date().getFullYear(),

            // Notification popover state.
            // Becomes dirty on the first change in the session; cleared on send.
            dirty: false,
            touchedSupplierIds: new Set(),
            sendingNotifications: false,
        };
    },

    computed: {
        notAllowed() {
            return this.$user.role_id > 4;
        },

        generalColorCode() {
            return this.getColor(this.selectedCategoryId);
        },

        yearOptions() {
            const cy = new Date().getFullYear();
            const years = [];
            for (let y = cy - 5; y <= cy; y++) years.push(y);
            return years;
        },
    },

    async mounted() {
        document.addEventListener('click', this._closeAllDropdowns);
        await this._getCategories();
    },

    beforeDestroy() {
        document.removeEventListener('click', this._closeAllDropdowns);
    },

    methods: {
        ...mapActions('KPIsMetrics', ['getCategories']),
        ...mapActions('admin', [
            'getSupplierMetricRequestsByCategory',
            'toggleSupplierMetricRequest',
            'setSupplierMetricRequestsFromYear',
            'notifySuppliersOfRequests',
        ]),

        // ----- bootstrapping -----

        async _getCategories() {
            try {
                const res = await this.getCategories();
                this.categories = res.data;
                if (this.categories.length > 0) {
                    await this.selectCategory(this.categories[0].sust_category_id);
                }
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'fetch_failed'));
            }
        },

        async selectCategory(id) {
            this.selectedCategoryId = id;
            await this._loadCategory(id);
        },

        async _loadCategory(id) {
            try {
                const res = await this.getSupplierMetricRequestsByCategory({ category_id: id });
                this.kpis = res.data.kpis || [];
                this.suppliers = res.data.suppliers || [];

                // The "from year" is primary-wide (same across categories); reflect the server value.
                if (res.data.from_year) {
                    this.requestFromYear = Number(res.data.from_year);
                }

                const assigned = new Map();
                const all = new Map();
                for (const kpi of this.kpis) {
                    for (const metric of (kpi.sust_metric_kpis || [])) {
                        const is_all = !!metric.all_suppliers;
                        all.set(metric.sust_metric_kpi_id, is_all);
                        // When the wildcard is on, leave individual assignments empty so that turning it
                        // off yields an empty selection rather than every supplier individually pinned.
                        assigned.set(
                            metric.sust_metric_kpi_id,
                            is_all ? new Set() : new Set(metric.assigned_supplier_ids || []),
                        );
                    }
                }
                this.assignedMap = assigned;
                this.allMap = all;
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'fetch_failed'));
            }
        },

        // ----- read helpers -----

        isAllSelected(metric_kpi_id) {
            return !!this.allMap.get(metric_kpi_id);
        },

        isAssigned(metric_kpi_id, supplier_company_id) {
            if (this.isAllSelected(metric_kpi_id)) return true;
            const set = this.assignedMap.get(metric_kpi_id);
            return !!(set && set.has(supplier_company_id));
        },

        assignedSuppliersForMetric(metric_kpi_id) {
            if (this.isAllSelected(metric_kpi_id)) return this.suppliers;
            return this.suppliers.filter(s => this.isAssigned(metric_kpi_id, s.company_id));
        },

        // ----- save-on-change -----

        async _toggle(payload) {
            try {
                await this.toggleSupplierMetricRequest(Object.assign({ from_year: this.requestFromYear }, payload));
                return true;
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'save_failed'));
                return false;
            }
        },

        async onSupplierToggle(metric_kpi_id, supplier_company_id) {
            if (this.isAllSelected(metric_kpi_id)) return; // "all" overrides individual toggles

            const currently = this.isAssigned(metric_kpi_id, supplier_company_id);
            const next = !currently;

            const set = this.assignedMap.get(metric_kpi_id) || new Set();
            if (next) set.add(supplier_company_id);
            else set.delete(supplier_company_id);
            this.assignedMap.set(metric_kpi_id, set);
            this.assignedMap = new Map(this.assignedMap);

            const ok = await this._toggle({ metric_kpi_id, supplier_company_id, on: next });
            if (ok) {
                this._markDirty(supplier_company_id);
            } else {
                if (next) set.delete(supplier_company_id);
                else set.add(supplier_company_id);
                this.assignedMap.set(metric_kpi_id, set);
                this.assignedMap = new Map(this.assignedMap);
            }
        },

        async onSelectAllToggle(metric_kpi_id) {
            const next = !this.isAllSelected(metric_kpi_id);

            this.allMap.set(metric_kpi_id, next);
            this.allMap = new Map(this.allMap);
            // Turning the wildcard on clears any individual pins for this metric.
            if (next) {
                this.assignedMap.set(metric_kpi_id, new Set());
                this.assignedMap = new Map(this.assignedMap);
            }

            const ok = await this._toggle({ metric_kpi_id, all: true, on: next });
            if (ok) {
                // Notify every current supplier the change affects.
                for (const s of this.suppliers) this._markDirty(s.company_id);
            } else {
                this.allMap.set(metric_kpi_id, !next);
                this.allMap = new Map(this.allMap);
            }
        },

        async onFromYearChange() {
            try {
                await this.setSupplierMetricRequestsFromYear({ from_year: this.requestFromYear });
                this._markDirty(null);
                for (const s of this.suppliers) this._markDirty(s.company_id);
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'save_failed'));
            }
        },

        // ----- notification popover -----

        _markDirty(supplier_company_id) {
            this.dirty = true;
            if (supplier_company_id !== null && supplier_company_id !== undefined) {
                this.touchedSupplierIds.add(supplier_company_id);
                this.touchedSupplierIds = new Set(this.touchedSupplierIds);
            }
        },

        async sendNotifications() {
            if (!this.dirty || this.sendingNotifications) return;
            this.sendingNotifications = true;
            const ids = Array.from(this.touchedSupplierIds);
            try {
                const res = await this.notifySuppliersOfRequests({ supplier_company_ids: ids });
                this.$notify('success', (res && res.data && res.data.messageName) || 'metric_assignment_process.notifications_sent');
                this.dirty = false;
                this.touchedSupplierIds = new Set();
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'save_failed'));
            } finally {
                this.sendingNotifications = false;
            }
        },

        // ----- misc -----

        toggleDropdown(metric_kpi_id) {
            if (this.openSupplierDropdownMetricId === metric_kpi_id) {
                this.openSupplierDropdownMetricId = null;
            } else {
                this.openSupplierDropdownMetricId = metric_kpi_id;
            }
        },

        _closeAllDropdowns() {
            this.openSupplierDropdownMetricId = null;
        },

        extractMessageName(err, fallback) {
            if (err && err.response && err.response.data && err.response.data.messageName) {
                return err.response.data.messageName;
            }
            return fallback;
        },

        getColor(id) {
            if (id == 1) return '#fa8a14';
            if (id == 2) return '#44a900';
            if (id == 3) return '#de0222';
            if (id == 4) return '#004578';
            return 'var(--sustainfinity-navy)';
        },

        categoryBg(category) {
            if (this.selectedCategoryId === category.sust_category_id) {
                return this.getColor(category.sust_category_id);
            }
            return 'whitesmoke';
        },

        categoryZIndex(category) {
            if (this.selectedCategoryId === category.sust_category_id) {
                return 5;
            }
            return 5 - category.sust_category_id;
        },
    },
};
</script>

<template>
    <div class="supplier-metric-requests-page">
        <div v-if="notAllowed">
            {{ $t('metric_assignments.directors_only') }}
        </div>

        <template v-else>
            <div class="description-bar">
                {{ $t('supplier_metric_requests.description') }}
            </div>

            <div class="categories-tabs-container" :style="{ 'border-bottom': `thick solid ${generalColorCode}` }">
                <div v-for="(category, index) in categories" :key="category.sust_category_id"
                    class="category flex-all-centered-col"
                    :class="{ 'selected-category': selectedCategoryId === category.sust_category_id }"
                    :style="{
                        backgroundColor: categoryBg(category),
                        color: getColor(category.sust_category_id),
                        zIndex: categoryZIndex(category),
                        transform: `translateX(${index * 85}%)`
                    }"
                    @click="selectCategory(category.sust_category_id)"
                >
                    {{ category.sust_category_name }}
                </div>
            </div>

            <Card width="100" style="margin-top: 0 !important;">
                <div class="smr-card-header">
                    <div class="from-year-control">
                        <span class="from-year-label">{{ $t('supplier_metric_requests.from_year_label') }}</span>
                        <select v-model.number="requestFromYear" @change="onFromYearChange">
                            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                        </select>
                    </div>
                </div>

                <div class="smr-table">
                    <div class="smr-row smr-header-row">
                        <div class="smr-cell smr-supplier-col">
                            {{ $t('supplier_metric_requests.headers.supplier') }}
                        </div>
                        <div class="smr-cell smr-metric-col">
                            {{ $t('supplier_metric_requests.headers.metrics') }}
                        </div>
                    </div>

                    <div v-for="kpi in kpis" :key="kpi.sust_kpi_id" class="smr-kpi-block">
                        <div class="smr-row smr-kpi-row">
                            <div class="smr-cell smr-supplier-col">-</div>
                            <div class="smr-cell smr-metric-col kpi-item-special" :style="{ color: generalColorCode }">
                                {{ kpi.sust_kpi_name }}
                            </div>
                        </div>

                        <div v-for="metric in kpi.sust_metric_kpis" :key="metric.sust_metric_kpi_id" class="smr-row smr-metric-row">
                            <div class="smr-cell smr-supplier-col">
                                <div class="supplier-picker" @click.stop="toggleDropdown(metric.sust_metric_kpi_id)">
                                    <span v-if="isAllSelected(metric.sust_metric_kpi_id)" class="supplier-chips">
                                        <span class="supplier-chip supplier-chip-all">
                                            {{ $t('supplier_metric_requests.all_suppliers_chip') }}
                                        </span>
                                    </span>
                                    <span v-else-if="assignedSuppliersForMetric(metric.sust_metric_kpi_id).length === 0" class="supplier-placeholder">
                                        {{ $t('supplier_metric_requests.pick_suppliers') }}
                                    </span>
                                    <span v-else class="supplier-chips">
                                        <span v-for="s in assignedSuppliersForMetric(metric.sust_metric_kpi_id)" :key="s.company_id" class="supplier-chip">
                                            {{ s.company_name }}
                                        </span>
                                    </span>
                                    <i class="fa-solid fa-caret-down"></i>

                                    <div v-if="openSupplierDropdownMetricId === metric.sust_metric_kpi_id" class="supplier-dropdown" @click.stop>
                                        <div v-if="suppliers.length === 0" class="supplier-dropdown-empty">
                                            {{ $t('supplier_metric_requests.no_suppliers') }}
                                        </div>
                                        <template v-else>
                                            <div class="supplier-option select-all-option">
                                                <input type="checkbox"
                                                    :id="`smr_all_${metric.sust_metric_kpi_id}`"
                                                    :checked="isAllSelected(metric.sust_metric_kpi_id)"
                                                    @change="onSelectAllToggle(metric.sust_metric_kpi_id)" />
                                                <label :for="`smr_all_${metric.sust_metric_kpi_id}`">
                                                    {{ $t('supplier_metric_requests.select_all_suppliers') }}
                                                </label>
                                            </div>
                                            <div v-for="s in suppliers" :key="s.company_id" class="supplier-option">
                                                <input type="checkbox"
                                                    :id="`smr_${metric.sust_metric_kpi_id}_${s.company_id}`"
                                                    :disabled="isAllSelected(metric.sust_metric_kpi_id)"
                                                    :checked="isAssigned(metric.sust_metric_kpi_id, s.company_id)"
                                                    @change="onSupplierToggle(metric.sust_metric_kpi_id, s.company_id)" />
                                                <label :for="`smr_${metric.sust_metric_kpi_id}_${s.company_id}`">{{ s.company_name }}</label>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <div class="smr-cell smr-metric-col metric-item-special">
                                {{ metric.sust_metric_kpi_name }}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <transition name="slide-up">
                <div v-if="dirty" class="notify-bar">
                    <span class="notify-text">{{ $t('supplier_metric_requests.notify_prompt') }}</span>
                    <button class="sust-btn sust-btn-green" :disabled="sendingNotifications" @click="sendNotifications">
                        <span v-if="sendingNotifications">{{ $t('supplier_metric_requests.sending') }}</span>
                        <span v-else>{{ $t('supplier_metric_requests.send_notifications') }}</span>
                    </button>
                </div>
            </transition>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.supplier-metric-requests-page {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 5em;

    > * {
        margin-top: 1em;

        &:first-of-type {
            margin-top: 0;
        }
    }
}

.description-bar {
    width: 90%;
    text-align: left;
    background-color: var(--sustainfinity-blue);
    color: whitesmoke;
    font-size: .8vw;
    padding: .5em;
    border-radius: .25em;
    box-shadow: 0 .1em .3em .05em gray;
}

.categories-tabs-container {
    height: 40px;
    width: 90%;
    position: relative;

    > .category {
        position: absolute;
        bottom: 0;
        height: 40px;
        width: 20%;
        font-weight: bold;
        font-size: .6vw;
        padding: 0 1em;
        text-align: center;
        border-top-right-radius: 150px;
        border-top-left-radius: 150px;
        border: .3em solid #d4dddf;
        border-bottom: 0;
        background-color: whitesmoke;
        transition: all 250ms;
        cursor: pointer;

        &:hover {
            filter: brightness(110%);
        }
    }

    > .selected-category {
        z-index: 99 !important;
        border: 0;
        color: white !important;
    }
}

.smr-card-header {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 0 .25em .75em .25em;
    border-bottom: thin solid #efefef;

    > .from-year-control {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: .5em;

        > .from-year-label {
            font-weight: bold;
            color: var(--sustainfinity-navy);
            font-size: .8vw;
        }

        > select {
            border: thin solid #ccc;
            border-radius: .35em;
            padding: .25em .5em;
            background-color: white;
            cursor: pointer;
            font-size: .8vw;

            &:focus {
                outline: thin solid var(--sustainfinity-navy);
            }
        }
    }
}

.smr-table {
    width: 100%;
    display: flex;
    flex-direction: column;

    > .smr-kpi-block {
        border-bottom: thin solid lightgray;
        padding: .25em 0;
    }
}

.smr-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: .5em 0;
    border-bottom: thin solid #efefef;

    &:last-child {
        border-bottom: 0;
    }

    > .smr-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 .5em;
    }

    .smr-supplier-col {
        flex: 4;
        position: relative;
        justify-content: flex-start;
    }

    .smr-metric-col {
        flex: 6;
        text-align: left;
        justify-content: flex-start;
    }
}

.smr-header-row {
    font-weight: bold;
    background-color: #f5f5f5;
}

.kpi-item-special {
    font-weight: bold;
    font-size: .8vw;
}

.metric-item-special {
    font-size: .7vw;
}

.supplier-picker {
    width: 100%;
    min-height: 2em;
    background-color: white;
    border: thin solid #ccc;
    border-radius: .4em;
    padding: .25em .5em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .25em;
    position: relative;

    > .supplier-placeholder {
        color: #999;
        font-size: .7vw;
        font-style: italic;
    }

    > .supplier-chips {
        display: flex;
        flex-wrap: wrap;
        gap: .25em;
        flex: 1;
        min-width: 0;

        > .supplier-chip {
            font-size: .65vw;
            background-color: var(--sustainfinity-blue);
            color: whitesmoke;
            padding: .15em .5em;
            border-radius: 1em;
            white-space: nowrap;

            &.supplier-chip-all {
                background-color: var(--sustainfinity-navy);
                font-weight: bold;
            }
        }
    }
}

.supplier-dropdown {
    position: absolute;
    top: calc(100% + .2em);
    left: 0;
    right: 0;
    z-index: 50;
    background-color: white;
    border: thin solid #ccc;
    border-radius: .4em;
    max-height: 16em;
    overflow-y: auto;
    box-shadow: 0 .25em .5em rgba(0, 0, 0, 0.12);

    > .supplier-dropdown-empty {
        padding: .75em;
        color: #999;
        font-style: italic;
        text-align: center;
        font-size: .75vw;
    }

    > .supplier-option {
        display: flex;
        align-items: center;
        gap: .5em;
        padding: .35em .75em;
        cursor: pointer;
        margin-bottom: 0;
        font-size: .75vw;

        &:hover {
            background-color: #f5f5f5;
        }

        > label {
            margin-bottom: 0;
            cursor: pointer;
        }

        &.select-all-option {
            font-weight: bold;
            border-bottom: thin solid #eee;
            position: sticky;
            top: 0;
            background-color: white;
        }
    }
}

.notify-bar {
    position: sticky;
    bottom: 1em;
    margin: 1em auto 0;
    width: 50%;
    max-width: 50%;
    z-index: 999;
    background-color: white;
    color: var(--sustainfinity-navy);
    padding: 1em 1.5em;
    border-radius: .5em;
    border: thin solid #e3e3e3;
    box-shadow: 0 .25em .75em rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1em;

    > .notify-text {
        flex: 1;
        min-width: 0;
    }
}

.slide-up-enter-active, .slide-up-leave-active {
    transition: transform 200ms ease, opacity 200ms ease;
}

.slide-up-enter, .slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}
</style>
